import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../reducers/authSlice';
import { Link, useLocation, useNavigate } from 'react-router';
import { signOut } from 'aws-amplify/auth';
import logo from '../../assets/images/Logo.png';
import { useLoader } from '../../loader/LoaderContext';
import {
  IconListDetails,
  IconLogout2,
  IconSettings,
  IconUserCircle,
} from '@tabler/icons-react';
import './ConsistentHeader.css';

// Get user initials from name
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

/**
 * ConsistentHeader Component
 * Provides a consistent top navigation bar across all pages
 * Matches the design of the UnifiedLanding page
 * 
 * @param {React.ReactNode} children - Optional content to display between logo and user menu (e.g., tabs)
 * @param {boolean} showTabs - Whether to show navigation tabs (for landing page)
 */
const ConsistentHeader = ({ children, showTabs = false }) => {
  const { showLoader, hideLoader } = useLoader();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const isCheckoutRoute = location.pathname.includes('/subscription');

  // Get user from Redux store
  const userAttributes = useSelector((state) => state.auth.userAttributes);
  const organizationData = useSelector((state) => state.auth.organization);
  const isBusinessUser = userAttributes['custom:signin_type'] === 'BUSINESS';
  const isAdmin = userAttributes['custom:role'] === 'org_admin';
  const isSuperAdmin = userAttributes['custom:role'] === 'super_admin';

  // Free trial banner
  const isFreeTrial = organizationData?.status === 'free_trial';
  const trialDaysRemaining = (() => {
    if (!isFreeTrial || !organizationData?.trial_end) return 0;
    const end = new Date(organizationData.trial_end);
    return Math.max(0, Math.ceil((end - new Date()) / (1000 * 60 * 60 * 24)));
  })();
  const showTrialBanner = isFreeTrial && !organizationData?.sub_force_allow && trialDaysRemaining > 0 && isBusinessUser && isAdmin && !isCheckoutRoute;
  const profileImage = userAttributes['profile'] ?? '';
  
  // Extract user info for dropdown header
  const userName = userAttributes.name ?? 
    `${userAttributes.given_name} ${userAttributes.family_name}`;
  const userEmail = userAttributes.email ?? '';
  const userInitials = getInitials(userName);

  const handleSignOut = async () => {
    try {
      showLoader();
      await signOut();
      dispatch(logout());
      navigate('/');
    } catch (error) {
      console.error('Error during sign out:', error);
    } finally {
      hideLoader();
    }
  };

  if (isCheckoutRoute) {
    return null;
  }

  return (
    <div className="sticky top-0 z-50">
    {showTrialBanner && (
      <div
        style={{
          background: trialDaysRemaining <= 3 ? '#fef2f2' : '#eff6ff',
          borderBottom: trialDaysRemaining <= 3 ? '1px solid #fecaca' : '1px solid #bfdbfe',
          padding: '8px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          fontSize: '13px',
        }}
      >
        <span style={{ color: trialDaysRemaining <= 3 ? '#b91c1c' : '#1d4ed8', fontWeight: 500 }}>
          {trialDaysRemaining > 0
            ? `\uD83D\uDD59 ${trialDaysRemaining} day${trialDaysRemaining !== 1 ? 's' : ''} remaining in your free trial`
            : '\u26A0\uFE0F Your free trial has ended'}
        </span>
        <button
          onClick={() => navigate('/subscription')}
          style={{
            background: trialDaysRemaining <= 3 ? '#ef4444' : '#2563eb',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            padding: '3px 12px',
            fontSize: '12px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Activate Subscription
        </button>
      </div>
    )}
    <div className="border-b bg-card shadow-sm consistent-header">
      <div className="max-w-[1280px] mx-auto">
        <div className="flex items-center justify-between gap-4">
          {/* Logo Section (and optional tabs/children) */}
          <div className="flex items-center gap-4 sm:gap-6 md:gap-8 flex-1 min-w-0">
            <Link to="/landing" className="shrink-0">
              <img src={logo} alt="PrescriptSure" className="py-3" />
            </Link>
            {children && <div className="flex-1 min-w-0 overflow-hidden">{children}</div>}
          </div>

          {/* User Profile Menu */}
          <div className="user-profile dropdown cst-dropdown shrink-0">
            <button
              className="btn dropdown-toggle"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              aria-label="User menu"
            >
              {profileImage ? (
                <span className="user-img">
                  <img src={profileImage} alt="Profile" />
                </span>
              ) : (
                <span className="user-img user-img-initials">
                  <span className="header-avatar-text">{userInitials}</span>
                </span>
              )}
              <span className="name">
                {userName}
              </span>
              <i className="ti ti-chevron-down"></i>
            </button>
            <ul className="dropdown-menu">
              {/* User Info Header */}
              <li className="dropdown-header">
                <div className="user-info">
                  <div className="user-name">{userName}</div>
                  <div className="user-email">{userEmail}</div>
                </div>
              </li>
              <li><hr className="dropdown-divider" /></li>

              {/* My Profile */}
              <li>
                <Link
                  to={'/settings/profile/details'}
                  className="dropdown-item"
                >
                  <IconUserCircle size={20} />
                  My Profile
                </Link>
              </li>

              {/* Organizational Settings */}
              {isBusinessUser && isAdmin && (
                <li>
                  <Link
                    to={'/settings/organization/details'}
                    className="dropdown-item"
                  >
                    <IconSettings size={20} />
                    Organizational Settings
                  </Link>
                </li>
              )}

              {/* Super Admin Panel */}
              {isSuperAdmin && (
                <li>
                  <Link to={'/super-admin'} className="dropdown-item">
                    <IconSettings size={20} />
                    Super Admin Panel
                  </Link>
                </li>
              )}

              {/* Support & Feedback */}
              {!isSuperAdmin && (
                <li>
                  <Link to={'/support/feedback'} className="dropdown-item">
                    <IconListDetails size={20} />
                    Support & Feedback
                  </Link>
                </li>
              )}

              {/* Logout */}
              <li>
                <button className="dropdown-item text-danger" onClick={handleSignOut}>
                  <IconLogout2 size={20} />
                  Logout
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};

export default ConsistentHeader;
